// Minimal subset of the mdast node shape that this module reads/writes.
// Not a complete mdast type — only the fields we touch.
export type MdNode = {
  type: string;
  value?: string;
  children?: MdNode[];
  data?: {hName?: string; hChildren?: MdNode[]};
};

// react-markdown drops backslash escapes while parsing, which would
// remove the \( \) \[ \] delimiters before remarkMath runs. preserveMathDelimiters
// gets called in the pre-parse (via `preprocess` of MarkdownTextPrimitive) to double
// the backslashes, which then get parsed back to what we need.
// eg: "\(" preprocesses to "\\(", which parses back to "\("

const CODE_REGION = /(`{3,}|~{3,})[\s\S]*?\1|(`+)[\s\S]*?\2/g; // Note: indented (4-space) code blocks are context-sensitive and not handled here; TODO: reassess this tradeoff if necessary
const ESCAPE_TARGETS = /\\([()[\]])/g;

const escapeDelimiters = (text: string): string =>
  text.replace(ESCAPE_TARGETS, '\\\\$1');

export const preserveMathDelimiters = (text: string): string => {
  const out: string[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  CODE_REGION.lastIndex = 0;
  // Skip code regions so any escape backslashes in codeblocks are preserved
  while ((m = CODE_REGION.exec(text)) !== null) {
    const beforeCode = text.slice(last, m.index);
    const codeRegion = m[0];
    out.push(escapeDelimiters(beforeCode), codeRegion);
    last = m.index + codeRegion.length;
  }
  const afterLastCode = text.slice(last);
  out.push(escapeDelimiters(afterLastCode));
  return out.join('');
};

// Matches block \[...\] and inline \(...\) math.
const PATTERN = /\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)/g;

const splitText = (value: string): MdNode[] => {
  const out: MdNode[] = [];
  let cursor = 0;
  let m: RegExpExecArray | null;
  PATTERN.lastIndex = 0;
  while ((m = PATTERN.exec(value)) !== null) {
    if (m.index > cursor) {
      out.push({type: 'text', value: value.slice(cursor, m.index)});
    }
    const isBlock = m[1] !== undefined;
    const src = (m[1] ?? m[2] ?? '').trim();
    out.push({
      type: isBlock ? 'mathBlock' : 'mathInline',
      data: {
        hName: isBlock ? 'math-block' : 'math-inline',
        hChildren: [{type: 'text', value: src}],
      },
    });
    cursor = m.index + m[0].length;
  }
  if (cursor < value.length) {
    out.push({type: 'text', value: value.slice(cursor)});
  }
  return out.length > 0 ? out : [{type: 'text', value}];
};

export const remarkMath = () => (tree: MdNode) => {
  const walk = (node: MdNode) => {
    if (!Array.isArray(node.children)) {
      return;
    }
    const next: MdNode[] = [];
    for (const child of node.children) {
      if (child.type === 'code' || child.type === 'inlineCode') {
        next.push(child);
      } else if (child.type === 'text' && typeof child.value === 'string') {
        next.push(...splitText(child.value));
      } else {
        walk(child);
        next.push(child);
      }
    }
    node.children = next;
  };
  walk(tree);
};
